import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Pill, X } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import { prescriptionsApi } from "../../shared/api/prescriptions";
import { medicineQueryKeys, medicinesApi } from "../../shared/api/medicines";
import type {
  EncounterPrescriptionItem,
} from "../../shared/types/encounter";
import type {
  MealTiming,
  MedicineSummaryResponse,
} from "../../shared/types/medicine";
import { mealTimingLabel, formatVnd } from "../../shared/format";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../shared/api/clinicFeatures";
import { apiError } from "../../shared/api/client";
import { ACTIVE_BRANCH_KEY } from "../../shared/api/branches";
import { SubstituteMedicineDialog } from "./components/SubstituteMedicineDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EncounterShell } from "./components/EncounterShell";
import { RxSearchPicker } from "./components/RxSearchPicker";

type Draft = Omit<EncounterPrescriptionItem, "id">;

export function EncounterPrescriptionPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/encounters" replace />;

  return (
    <EncounterShell encounterId={id}>
      {(e) => (
        <PrescriptionEditor
          encounterId={id}
          initial={e.prescription.items}
          readOnly={e.status !== "IN_PROGRESS"}
        />
      )}
    </EncounterShell>
  );
}

function PrescriptionEditor({
  encounterId,
  initial,
  readOnly,
}: {
  encounterId: string;
  initial: EncounterPrescriptionItem[];
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft[]>(initial.map(stripId));
  const [error, setError] = useState<string | null>(null);
  // #18535: highlight missing-quantity rows only after the user tries to finalize.
  const [attempted, setAttempted] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => setDraft(initial.map(stripId)), [initial]);

  const routes = useQuery({
    queryKey: medicineQueryKeys.routes(),
    queryFn: () => medicinesApi.listRoutes(),
  });

  // Pharmacy-linked prescribing (ADR-0027 Phase 3): show đơn giá/thành tiền when
  // the clinic enabled "Hiển thị giá thuốc trên toa". Prices come from the
  // medicine catalog, keyed by medicineId so new + existing lines are consistent.
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const showPrice = features.data?.showPriceOnRx === true;
  const meds = useQuery({
    queryKey: ["medicines", "price-map"],
    queryFn: () => medicinesApi.list(undefined, { page: 0, size: 500 }),
    enabled: showPrice,
    staleTime: 5 * 60 * 1000,
  });
  const priceMap = useMemo(() => {
    const m = new Map<string, number>();
    (meds.data?.content ?? []).forEach((x) => m.set(x.id, x.sellPriceVnd));
    return m;
  }, [meds.data]);
  const priceOf = (medicineId: string) => priceMap.get(medicineId) ?? 0;
  const grandTotal = showPrice
    ? draft.reduce((s, it) => s + priceOf(it.medicineId) * (it.quantity || 0), 0)
    : 0;

  const navigate = useNavigate();

  const save = useMutation({
    mutationFn: () => encountersApi.replacePrescription(encounterId, draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) });
      setError(null);
    },
    onError: (e) => setError(apiError(e).detail),
  });

  // Promote the saved encounter draft into a real prescription (T33) and
  // open its detail page. Backend requires every line to reference a
  // catalogue medicine; it returns a clear Vietnamese error otherwise.
  const createRx = useMutation({
    mutationFn: () => prescriptionsApi.createFromEncounter(encounterId),
    onSuccess: (rx) => navigate(`/prescriptions/${rx.id}`),
    onError: (e) => setError(apiError(e).detail),
  });

  function update(idx: number, patch: Partial<Draft>) {
    setDraft((cur) => cur.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }
  /** Which line is looking for a substitute, if any. */
  const [substituteFor, setSubstituteFor] = useState<number | null>(null);
  const activeBranchId =
    typeof window === "undefined" ? null : window.localStorage.getItem(ACTIVE_BRANCH_KEY);

  function remove(idx: number) {
    setDraft((cur) => cur.filter((_, i) => i !== idx));
  }
  function addFromMedicine(m: MedicineSummaryResponse) {
    setDraft((cur) => [
      ...cur,
      {
        medicineId: m.id,
        medicineName: m.name,
        medicineUnit: m.unit,
        medicineStrength: m.strength,
        quantity: 0,
        doseMorning: 0,
        doseNoon: 0,
        doseAfternoon: 0,
        doseEvening: 0,
        doseUnit: m.unit,
        route: "ORAL",
        mealTiming: null,
        note: null,
      },
    ]);
  }

  const dirty = useMemo(
    () =>
      JSON.stringify(draft) !== JSON.stringify(initial.map(stripId)),
    [draft, initial]
  );

  // #18535: Số lượng (quantity) is required by the backend for every line. Validate
  // it on the UI before submitting so the user gets a field-level message instead of
  // a server error. (Số liều / doses is NOT enforced here — that rule is unconfirmed.)
  const invalidQtyNames = useMemo(
    () => draft.filter((d) => !(d.quantity > 0)).map((d) => d.medicineName),
    [draft]
  );

  /** Validate then create the official prescription (blocks submit if a quantity is missing). */
  function finalize() {
    setAttempted(true);
    if (invalidQtyNames.length > 0) {
      setError(
        `Vui lòng nhập số lượng (> 0) cho: ${invalidQtyNames.join(", ")}.`
      );
      return;
    }
    setError(null);
    createRx.mutate();
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          Đơn thuốc
        </h2>
        <div className="flex flex-wrap gap-2">
          {!readOnly && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPickerOpen(true)}
            >
              <Pill aria-hidden="true" /> Thêm toa mẫu / thuốc
            </Button>
          )}
          {dirty && !readOnly && (
            <Button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? "Đang lưu…" : "Lưu đơn"}
            </Button>
          )}
          {!readOnly && draft.length > 0 && (
            <Button
              type="button"
              onClick={finalize}
              disabled={dirty || createRx.isPending}
              title={
                dirty ? "Lưu đơn trước khi kê đơn chính thức" : undefined
              }
            >
              {createRx.isPending ? "Đang kê đơn…" : "Kê đơn chính thức"}
            </Button>
          )}
        </div>
      </div>

      {draft.length === 0 && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-dashed border-border bg-muted/40 p-4">
          <Pill className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="text-sm">
            <strong className="font-semibold text-foreground">Chưa kê thuốc nào.</strong>
            <p className="mt-1 text-muted-foreground">
              Bấm "Dùng toa thuốc mẫu" để áp dụng tổ hợp đã lưu, hoặc "Thêm
              thuốc" để chọn từng món.
            </p>
          </div>
        </div>
      )}

      {draft.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[2rem_minmax(180px,1fr)_repeat(4,3.5rem)_minmax(140px,1fr)_2.5rem] items-center gap-2 border-b border-border pb-2 text-xs font-medium text-muted-foreground">
              <span></span>
              <span>Thuốc</span>
              <span className="text-center">Sáng</span>
              <span className="text-center">Trưa</span>
              <span className="text-center">Chiều</span>
              <span className="text-center">Tối</span>
              <span>Đường / Bữa</span>
              <span></span>
            </div>
            {draft.map((it, i) => (
              <div key={i} className="border-b border-border last:border-b-0">
                <div className="grid grid-cols-[2rem_minmax(180px,1fr)_repeat(4,3.5rem)_minmax(140px,1fr)_2.5rem] items-start gap-2 py-3">
                  <span className="pt-1.5 text-sm font-semibold text-muted-foreground">
                    {i + 1}
                  </span>
                  <div>
                    <strong className="text-sm font-semibold text-foreground">
                      {it.medicineName}
                    </strong>
                    {it.medicineStrength && (
                      <span className="text-sm text-muted-foreground">
                        {" "}· {it.medicineStrength}
                      </span>
                    )}
                    {/* Hết thuốc thì đổi ngay tại đây, không phải rời màn hình kê đơn. */}
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-1 h-6 px-1.5 text-[11px]"
                        onClick={() => setSubstituteFor(i)}
                        title="Tìm thuốc khác cùng hoạt chất"
                      >
                        <ArrowLeftRight className="mr-1 size-3" />
                        Thay thế
                      </Button>
                    )}
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">SL:</span>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={it.quantity}
                        onChange={(e) =>
                          update(i, { quantity: Number(e.target.value) || 0 })
                        }
                        disabled={readOnly}
                        aria-invalid={attempted && !(it.quantity > 0)}
                        className={
                          attempted && !(it.quantity > 0)
                            ? "h-8 w-20 border-destructive focus-visible:ring-destructive"
                            : "h-8 w-20"
                        }
                      />
                      <span className="text-xs">{it.medicineUnit}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        onClick={() =>
                          update(i, {
                            quantity: Math.ceil(
                              (it.doseMorning + it.doseNoon + it.doseAfternoon + it.doseEvening) * 7
                            ),
                          })
                        }
                        disabled={readOnly}
                        title="Tự tính = (sáng+trưa+chiều+tối) × 7 ngày"
                      >
                        Tự tính 7n
                      </Button>
                    </div>
                    {showPrice && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Đơn giá {formatVnd(priceOf(it.medicineId))}₫ · Thành tiền{" "}
                        <strong className="text-foreground">
                          {formatVnd(priceOf(it.medicineId) * (it.quantity || 0))}₫
                        </strong>
                      </div>
                    )}
                  </div>
                  <DoseInput
                    value={it.doseMorning}
                    onChange={(v) => update(i, { doseMorning: v })}
                    disabled={readOnly}
                  />
                  <DoseInput
                    value={it.doseNoon}
                    onChange={(v) => update(i, { doseNoon: v })}
                    disabled={readOnly}
                  />
                  <DoseInput
                    value={it.doseAfternoon}
                    onChange={(v) => update(i, { doseAfternoon: v })}
                    disabled={readOnly}
                  />
                  <DoseInput
                    value={it.doseEvening}
                    onChange={(v) => update(i, { doseEvening: v })}
                    disabled={readOnly}
                  />
                  <div className="flex flex-col gap-1">
                    <select
                      value={it.route ?? ""}
                      onChange={(e) => update(i, { route: e.target.value || null })}
                      disabled={readOnly}
                      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">—</option>
                      {routes.data?.map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.labelVi}
                        </option>
                      ))}
                    </select>
                    <select
                      value={it.mealTiming ?? ""}
                      onChange={(e) =>
                        update(i, {
                          mealTiming: (e.target.value || null) as MealTiming | null,
                        })
                      }
                      disabled={readOnly}
                      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">— Bữa ăn —</option>
                      {(Object.keys(mealTimingLabel) as MealTiming[]).map((m) => (
                        <option key={m} value={m}>
                          {mealTimingLabel[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!readOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-9"
                      onClick={() => remove(i)}
                      aria-label="Xoá thuốc"
                    >
                      <X aria-hidden="true" />
                    </Button>
                  ) : (
                    <span />
                  )}
                </div>
                {(it.note || !readOnly) && (
                  <div className="mb-3 ml-9">
                    <Input
                      type="text"
                      value={it.note ?? ""}
                      onChange={(e) => update(i, { note: e.target.value || null })}
                      placeholder="Ghi chú dùng thuốc — VD: cách 6 giờ, ngừng khi hết triệu chứng"
                      disabled={readOnly}
                      className="h-8 text-[13px]"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showPrice && draft.length > 0 && (
        <div className="mt-3 border-t pt-3 text-right text-sm">
          Tổng tiền thuốc:{" "}
          <strong className="text-base text-foreground">{formatVnd(grandTotal)}₫</strong>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <RxSearchPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPickTemplate={(template) => {
          setDraft(
            template.items.map((it) => ({
              medicineId: it.medicineId,
              medicineName: it.medicineName,
              medicineUnit: it.medicineUnit,
              medicineStrength: it.medicineStrength,
              quantity: it.quantity,
              doseMorning: it.doseMorning,
              doseNoon: it.doseNoon,
              doseAfternoon: it.doseAfternoon,
              doseEvening: it.doseEvening,
              doseUnit: it.doseUnit ?? it.medicineUnit,
              route: it.route,
              mealTiming: it.mealTiming,
              note: it.note,
            }))
          );
        }}
        onPickMedicine={addFromMedicine}
      />

      {substituteFor !== null && draft[substituteFor] && (
        <SubstituteMedicineDialog
          medicineId={draft[substituteFor].medicineId}
          medicineName={draft[substituteFor].medicineName}
          branchId={activeBranchId}
          onClose={() => setSubstituteFor(null)}
          onPick={(sub) => {
            const idx = substituteFor;
            // Replace the medicine, keep the line. Dose and quantity are deliberately NOT carried
            // over blindly when the strength differs — the numbers were chosen for the old
            // medicine, and silently reusing them is how a substitution becomes a dosing error.
            update(idx, {
              medicineId: sub.medicineId,
              medicineName: sub.name,
              medicineUnit: sub.unit ?? draft[idx].medicineUnit,
              medicineStrength: sub.strength ?? null,
              ...(sub.sameStrength
                ? {}
                : {
                    quantity: 0,
                    doseMorning: 0,
                    doseNoon: 0,
                    doseAfternoon: 0,
                    doseEvening: 0,
                  }),
            });
          }}
        />
      )}
    </Card>
  );
}

function DoseInput({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
}) {
  return (
    <Input
      type="number"
      min={0}
      step={0.5}
      value={value || ""}
      placeholder="0"
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      disabled={disabled}
      className="h-9 px-1 text-center"
    />
  );
}

function stripId(it: EncounterPrescriptionItem): Draft {
  const { id: _id, ...rest } = it;
  void _id;
  return rest;
}
